/**
 * `integrationsApi` — standalone RTK Query slice backing the
 * Settings → Integrations tab.
 *
 * It wraps two related proxy-server surfaces:
 *
 *   • Secrets    — `/api/v1/ontology/secrets` (the `/api/v1/ontology`
 *                  graph pass-through that `ontologyBaseQuery` is rooted
 *                  at, so these endpoints use relative `/secrets` URLs).
 *                  Metadata-only: there is no endpoint that returns a
 *                  secret's plaintext value, so the cache only ever holds
 *                  `Secret` metadata. Treat values as write-only —
 *                  create / rotate / delete, never read.
 *
 *   • Integrations — `/api/v1/integrations` (a top-level proxy resource,
 *                  not under `/api/v1/ontology`), so these endpoints use
 *                  the absolute `PROXY_API_V1_URL` to avoid RTK Query
 *                  re-rooting them under `/api/v1/ontology/...` — the same
 *                  pattern as other top-level proxy-backed slices.
 *
 * The two surfaces share one slice (and one cache) because the
 * Integrations UI always orchestrates them together: creating an
 * integration first writes the API key as a secret, then records the
 * integration that references it by logical name. The credential
 * orchestration (create secret → create integration, with secret
 * rollback on failure) lives in the UI hook
 * (`useIntegrationCredentialFlows`), not here, so each endpoint stays a
 * thin, individually-testable wrapper over one HTTP call.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { PROXY_API_V1_URL, ontologyBaseQuery } from '../core'
import type {
  CreateIntegrationPayload,
  CreateSecretPayload,
  Integration,
  IntegrationListFilters,
  Secret,
  SecretListFilters,
  UpdateIntegrationPayload,
  UpdateSecretPayload
} from './types'

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

export const INTEGRATIONS_TAG_TYPES = ['Integration', 'Secret'] as const
export type IntegrationsTagType = (typeof INTEGRATIONS_TAG_TYPES)[number]

export const integrationsApi = createApi({
  reducerPath: 'integrationsApi',
  baseQuery: ontologyBaseQuery,
  refetchOnReconnect: true,
  tagTypes: INTEGRATIONS_TAG_TYPES,
  endpoints: (builder) => ({
    // ─── Integrations (proxy-server, `${PROXY_API_V1_URL}/integrations`) ──
    listIntegrations: builder.query<Integration[], IntegrationListFilters | void>({
      query: (filters) => ({
        url: `${PROXY_API_V1_URL}/integrations`,
        params: (filters ?? undefined) as Record<string, string | number | undefined>
      }),
      transformResponse: (
        response: {
          integrations: Integration[]
          next_page_token?: string
          total_size?: number
        } | null
      ) => asArray<Integration>(response?.integrations),
      providesTags: (result) =>
        result
          ? [
              ...result.map((i) => ({ type: 'Integration' as const, id: i.id })),
              { type: 'Integration' as const, id: 'LIST' }
            ]
          : [{ type: 'Integration' as const, id: 'LIST' }]
    }),
    getIntegration: builder.query<Integration, string>({
      query: (id) => `${PROXY_API_V1_URL}/integrations/${encodeURIComponent(id)}`,
      providesTags: (_r, _e, id) => [{ type: 'Integration', id }]
    }),
    createIntegration: builder.mutation<Integration, CreateIntegrationPayload>({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/integrations`,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'Integration', id: 'LIST' }]
    }),
    updateIntegration: builder.mutation<
      Integration,
      { id: string; patch: UpdateIntegrationPayload }
    >({
      query: ({ id, patch }) => ({
        url: `${PROXY_API_V1_URL}/integrations/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: patch
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Integration', id },
        { type: 'Integration', id: 'LIST' }
      ]
    }),
    deleteIntegration: builder.mutation<void, string>({
      query: (id) => ({
        url: `${PROXY_API_V1_URL}/integrations/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Integration', id },
        { type: 'Integration', id: 'LIST' }
      ]
    }),

    // ─── Secrets (proxy-server, `/api/v1/ontology/secrets`) ───────────────
    //
    // Relative URLs (resolved against `ontologyBaseQuery`'s
    // `/api/v1/ontology` root). Metadata-only — no value is ever returned.
    listSecrets: builder.query<Secret[], SecretListFilters | void>({
      query: (filters) => ({
        url: '/secrets',
        params: (filters ?? undefined) as Record<string, string | number | undefined>
      }),
      transformResponse: (
        response: {
          secrets: Secret[]
          next_page_token?: string
        } | null
      ) => asArray<Secret>(response?.secrets),
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: 'Secret' as const, id: s.id })),
              { type: 'Secret' as const, id: 'LIST' }
            ]
          : [{ type: 'Secret' as const, id: 'LIST' }]
    }),
    getSecret: builder.query<Secret, string>({
      query: (id) => `/secrets/${encodeURIComponent(id)}`,
      providesTags: (_r, _e, id) => [{ type: 'Secret', id }]
    }),
    createSecret: builder.mutation<Secret, CreateSecretPayload>({
      query: (body) => ({
        url: '/secrets',
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'Secret', id: 'LIST' }]
    }),
    // Rotate: replaces the encrypted value and bumps `version`.
    updateSecret: builder.mutation<Secret, { id: string; patch: UpdateSecretPayload }>({
      query: ({ id, patch }) => ({
        url: `/secrets/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: patch
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Secret', id },
        { type: 'Secret', id: 'LIST' }
      ]
    }),
    deleteSecret: builder.mutation<void, string>({
      query: (id) => ({
        url: `/secrets/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Secret', id },
        { type: 'Secret', id: 'LIST' }
      ]
    })
  })
})

export const {
  useListIntegrationsQuery,
  useGetIntegrationQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useListSecretsQuery,
  useLazyListSecretsQuery,
  useGetSecretQuery,
  useCreateSecretMutation,
  useUpdateSecretMutation,
  useDeleteSecretMutation
} = integrationsApi
